<script setup lang="ts">
import {computed, nextTick, ref, watch} from 'vue';
import {storeToRefs} from 'pinia';
import BalmSnackbar from '@/vue/molecules/balm-snackbar.vue';
import BalmToast from '@/vue/molecules/balm-toast.vue';
import {TOAST_DURATION_MS, useSnackbarStore} from '@/ts/store/snackbar-store';

/**
 * Where every toast and snackbar the app raises is shown (IPD-3328). Mounted once for the whole
 * signed-in app rather than by each page, so a confirmation raised just before a navigation is
 * still there after it, and the page that raised it needs no markup of its own.
 */
const snackbarStore = useSnackbarStore();
const {current, currentToast} = storeToRefs(snackbarStore);

// The store keeps at most one of the two up, so whichever is up is the one the reader hears.
const shown = computed<{id: number; message: string} | null>(() => current.value ?? currentToast.value);

/**
 * Read by the permanent live region below; empty while nothing is up, so it only ever speaks when
 * something is raised. Emptied first and filled on the next tick, so a showing that repeats the
 * message on screen is announced again rather than taken for no change.
 */
const announcement = ref<string>('');

watch(() => shown.value?.id, () => {
  announcement.value = '';
  const message = shown.value?.message;
  if (message) {
    void nextTick(() => {
      announcement.value = message;
    });
  }
});
</script>

<template>
  <div class="snackbar-host">
    <!--
      The live region is mounted for good and only its text changes. A region inserted together with
      its content is the case ARIA leaves to the user agent, and several of them announce nothing —
      which is why every other live region in this app is a permanent node too.
    -->
    <p class="sr-only" role="status">
      {{ announcement }}
    </p>
    <transition name="feedback">
      <!-- Keyed by showing, so a replacement is a fresh one with its own countdown. -->
      <balm-toast
        v-if="currentToast"
        :key="currentToast.id"
        class="sh-toast"
        :message="currentToast.message"
        :duration-ms="TOAST_DURATION_MS"
        @close="snackbarStore.hideToast(currentToast.id)"
      />
    </transition>
    <transition name="feedback">
      <balm-snackbar
        v-if="current"
        :key="current.id"
        class="sh-snackbar"
        :message="current.message"
        :action="current.action"
        :dismissible="current.dismissible"
        :duration-ms="current.durationMs"
        @close="snackbarStore.hide(current.id)"
        @navigate="snackbarStore.hide(current.id)"
      />
    </transition>
  </div>
</template>

<style scoped lang="scss">
// Over the page and its sticky parts, under Quasar's dialogs (6000) so a modal opened while one is
// up covers it rather than the other way round.
$feedback-z: 5000;

// The design system's toast sits at the bottom centre and hugs its message, up to 920px.
.sh-toast {
  position: fixed;
  // 24px from the bottom on a phone and 32px from tablet up, as the design system places it.
  bottom: var(--Spacing-spacing-6);
  left: 50%;
  z-index: $feedback-z;
  width: max-content;
  max-width: min(920px, calc(100vw - 2 * var(--Spacing-spacing-5)));
  transform: translateX(-50%);

  @include screen-lg {
    bottom: var(--Spacing-spacing-5);
  }
}

// The snackbar sits at the bottom right, and spans the screen on a phone.
.sh-snackbar {
  position: fixed;
  right: var(--Spacing-spacing-5);
  bottom: var(--Spacing-spacing-5);
  left: var(--Spacing-spacing-5);
  z-index: $feedback-z;

  @include screen-sm {
    left: auto;
    width: 356px;
  }
}

.feedback-enter-active,
.feedback-leave-active {
  // The app's own curve: nearly all of the time goes into the deceleration, so it reads as already
  // arriving rather than setting off — which is what a confirmation should do.
  transition: opacity 200ms cubic-bezier(0.16, 1, 0.3, 1), translate 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

// `translate` rather than `transform`, so the toast's own centring transform is left alone.
.feedback-enter-from,
.feedback-leave-to {
  opacity: 0;
  translate: 0 var(--Spacing-spacing-3);
}

@media (prefers-reduced-motion: reduce) {
  .feedback-enter-active,
  .feedback-leave-active {
    transition: none;
  }
}
</style>
